import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { CARD_TYPES } from 'src/features/cards/types';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import { GET_PRICING_PLAN_POINTS } from 'src/features/pricingPlans/api/useGetPricingPlansPoints';
import { useListPricingPlans } from 'src/features/pricingPlans/api/useListPricingPlans';
import {
    updatePointPricingPlanFormSchemaValues,
    type UpdatePointPricingPlanFormSchemaValues,
    useUpdatePointPricingPlan,
} from 'src/features/pricingPlans/api/useUpdatePointPricingPlan';
import { pricingPlansMessages } from 'src/features/pricingPlans/utils/messages';
import usePagination from 'src/hooks/usePagination';
import { queryClient } from 'src/lib/react-query';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { useToast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { PricingPlanPoint } from 'src/features/pricingPlans/types';

interface EditPricingPlanByPointProps {
    selectedPricingPlanPoint: PricingPlanPoint;
    isEditModalOpen: boolean;
    setIsEditModalOpen: (isOpen: boolean) => void;
}

export function EditPricingPlanByPoint({
    isEditModalOpen,
    setIsEditModalOpen,
    selectedPricingPlanPoint,
}: EditPricingPlanByPointProps) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();
    const { queryPaginationOptions } = usePagination();
    const { toast } = useToast();

    const { data: pricingPlans } = useListPricingPlans({
        cardType: CARD_TYPES.NORMAL,
        parkingId: parkingId || '',
        config: { enabled: !!parkingId },
        ...queryPaginationOptions,
    });

    const form = useForm<UpdatePointPricingPlanFormSchemaValues>({
        resolver: zodResolver(updatePointPricingPlanFormSchemaValues),
        defaultValues: {
            price_plan_id: selectedPricingPlanPoint.price_plan_id,
        },
    });

    const { mutate, isPending } = useUpdatePointPricingPlan({
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: [GET_PRICING_PLAN_POINTS],
            });
            setIsEditModalOpen(false);
            form.reset();
            toast({
                title: formatMessage(pricingPlansMessages.pricingPlanUpdated),
                description: formatMessage(pricingPlansMessages.pricingPlanUpdatedSuccessfully),
            });
        },
        onError: (e) => {
            const error = e?.response?.data.all ?? [];
            const errorMessage = error?.[0]
                ? error?.[0].message
                : formatMessage(generalErrorMessages.somethingWentWrongRetry);

            toast({
                variant: 'destructive',
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description: errorMessage,
            });
        },
    });

    const onSubmit: SubmitHandler<UpdatePointPricingPlanFormSchemaValues> = (
        values: UpdatePointPricingPlanFormSchemaValues,
    ) => {
        if (!parkingId) return;
        const { price_plan_id } = values;
        mutate({
            parkingId,
            pointId: selectedPricingPlanPoint.id,
            price_plan_id: Number(price_plan_id),
        });
    };

    return (
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen} modal>
            <DialogContent>
                <DialogHeader>
                    <div className="flex gap-4">
                        <SquareIcon icon={ICON_MAP.EDIT} />
                        <div>
                            <DialogTitle>{formatMessage(pricingPlansMessages.editPricingPlan)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(pricingPlansMessages.editPricingPlanPoint)}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <DialogSeparator />

                <DialogBody>
                    <Form form={form} variant="dialog" onSubmit={form.handleSubmit(onSubmit)}>
                        <FormField
                            control={form.control}
                            name="price_plan_id"
                            render={({ field }) => (
                                <FormItem variant="dialog">
                                    <FormLabel variant="dialog" required>
                                        {formatMessage(pricingPlansMessages.pricingPlan)}
                                    </FormLabel>
                                    <FormControlStack className="mr-3">
                                        <FormControl>
                                            <Select onValueChange={field.onChange} value={field.value?.toString()}>
                                                <SelectTrigger className="w-full">
                                                    <SelectValue
                                                        placeholder={formatMessage(generalMessages.selectValue)}
                                                    />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {pricingPlans?.results.map((plan) => (
                                                        <SelectItem key={plan.id} value={plan.id.toString()}>
                                                            {plan.plan_name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                </FormItem>
                            )}
                        />
                    </Form>
                </DialogBody>

                <DialogSeparator />

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>

                    <Button
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                        disabled={!form.formState.isDirty}
                    >
                        {isPending && <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />}
                        {formatMessage(pricingPlansMessages.editPricingPlan)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
